import React, { useEffect, useRef, useMemo } from 'react';

import * as echarts from 'echarts/core';
import { SankeyChart } from 'echarts/charts';
import { GridComponent } from 'echarts/components';
import { UniversalTransition } from 'echarts/features';
import { CanvasRenderer } from 'echarts/renderers';

import { getLabel } from "../functions/lang";

echarts.use([GridComponent, SankeyChart, CanvasRenderer, UniversalTransition]);

const SankeyEChart = ({ json, language }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
  
    const {nodes, links, zoom, center} = useMemo(() => {
        let data = json.data;
        data = data.map(item => ({
            ...item,
            source: getLabel(item.source, language),
            target: getLabel(item.target, language),
            unit: getLabel(item.unit, language),
            product_name: getLabel(item.product_name, language),
        }));
        let positions = JSON.parse(json.sankey?.draft_positions ?? '{}');
        
        let zoom = positions["zoom"] || 1;
        let center = positions["center"] || ['50%', '50%'];

        let nodeNames = new Set();
        data.forEach(item => {
            nodeNames.add(item.source);
            nodeNames.add(item.target);
        });

        let sources = data.map(d => d.source);
        let targets = data.map(d => d.target);

        let nodes = Array.from(nodeNames).map(name => {
            const unit = data.find(d => d.source === name || d.target === name)?.unit || '';
            if (!sources.includes(name) && targets.includes(name)) {
                let target_en = data.find(d => d.target === name).target_en || name;
                return { name, node_id: target_en, itemStyle: { color: "#cccccc" }, unit };
            } else if (sources.includes(name) && !targets.includes(name)) {
                let source_en = data.find(d => d.source === name).source_en || name;
                return { name, node_id: source_en, itemStyle: { color: "#cccccc" }, unit }
            } else { 
                let firstLink = data.find(d => d.source === name);
                return { name, node_id: firstLink.source_en, itemStyle: { color: firstLink.color }, unit };
            }
        });

        nodes = nodes.map(n => {
            return positions[n.node_id] ? { 
                ...n, 
                localX: positions[n.node_id].x, 
                localY: positions[n.node_id].y 
            }: { ...n }; 
        }).sort((a, b) => a.node_id.localeCompare(b.node_id));

        let links = data.map(d => {
            return {
                ...d,
                value: Math.abs(d.value), //all negative value to positive
                lineStyle: {
                    color: d.color,
                    opacity: 0.8
                }
            }
        }).sort((a, b) => a.source_en.localeCompare(b.source_en));
        return {nodes, links, zoom, center};
    }, [json, language]);

    useEffect(() => {
        if (!chartRef.current) return;

        if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current);
        }

        const option = {
            tooltip: {
                trigger: 'item',
                triggerOn: 'mousemove',
                formatter: function (params) {
                        const data = params.data || {};
                        const source = data.source || null;
                        const target = data.target || null;
                        const value = data.value || null;
                        const unit = data.unit || '';
                        const product = data.product_name || params.name;
                        const color = data.color || '#cccccc';

                        const colorCircle = `
                            <span style="display:inline-block;
                            width:10px; height:10px;
                            background:${color}; border-radius:50%;
                            margin-right:6px;
                            border:1px solid #ccc;
                            vertical-align:middle;
                            "></span>
                        `;
                        if (source && target) {
                            return `
                                <div style="font-size:13px; line-height:1.5">
                                <strong>${source} → ${target}</strong><br>
                                ${value.toFixed(2)} ${unit}
                                <br>
                                ${colorCircle} ${product}
                                </div>
                            `;
                        }

                    return `<div style="font-size:13px; line-height:1.5">
                    <strong>${params.name}</strong><br>${params.value.toFixed(2)} ${data.unit}<br>
                                ${colorCircle} ${product}</div>`;
                    },
            },
            series: [
                {
                    type: 'sankey',
                    top: '20%',
                    bottom: '20%',
                    layoutIterations: 0,
                    draggable: true,
                    data: nodes,
                    links: links,
                    levels: [],
                    lineStyle: {
                        curveness: 0.5
                    },
                    animation: true,
                    animationDuration: 800,
                    zoom: zoom,
                    center: center,
                    roam: 'scale',
                    label: {
                        formatter:  function (params) {
                            const { unit } = params.data;
                            if (params.value !== undefined) {
                                return `${params.name} \n ${parseFloat(params.value).toFixed(2)} ${unit}`;
                            }
                            return params.name;
                        },
                        overflow: 'break',
                        width: 150,
                        fontSize: 9,
                        lineHeight: 11,
                        textBorderWidth: 2,
                        position: 'bottom'
                    }
                }
            ]
        };

        chartInstance.current.setOption(option, true, true);

        const handleResize = () => chartInstance.current?.resize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [nodes, links, zoom, center]); 

    

    return <div ref={chartRef} style={{ maxWidth: '100%', height: '600px', marginTop: '5px', border: '1px solid #ccc', borderRadius: '5px'}}/>;
};

export default SankeyEChart;